<template>
  <div class="lisaui:flex lisaui:justify-between" @click.stop>
    <InputRow v-slot="{ validation: { valid } }" :model-value="selectedType" name="type">
      <LISASelect
        v-model="selectedType"
        hide-label
        :invalid="valid === false"
        class="mr-2"
        :placeholder="t('global.all')"
        :options="typeOptions"
      />
    </InputRow>
    <InputRow v-slot="{ validation: { valid } }" :model-value="selectedIDs" name="ids">
      <LISASelect
        v-model="selectedIDs"
        hide-label
        class="w-100"
        multiple
        selection-display="chips"
        :invalid="valid === false"
        :options="idOptions"
        @click.stop
      />
    </InputRow>
  </div>
</template>

<script setup lang="ts">
import { useTranslation } from "@/language/translation";

import InputRow from "@/components/shared/form/InputRow.vue";
import { computed, inject, onMounted, ref, watch } from "vue";
import { LISASelect, type LISASelectOption } from "@fi-solutions/lisa-ui";
import IDFilter from "../filterImplementations/IDFilter";
import { BoardFilterOptionsInjectionKey, FilterEntity, FilterUIProps } from "../FilterTypes";
import { useSQDCDisplayName } from "../../../../helper/SQDCDisplayNameHelper";

const props = defineProps(FilterUIProps);
const displayName = useSQDCDisplayName();
const boardFilterOptions = inject(BoardFilterOptionsInjectionKey);

const selectedIDs = ref<number[]>([]);
const selectedType = ref<number>();

const typeOptions = computed<LISASelectOption[]>(() =>
  props.types.map((type) => ({
    id: type.id,
    label: displayName(type),
    icon: type.icon ?? (props.mode === FilterEntity.EVENT ? "far fa-comment" : "fas fa-list-check"),
  })),
);

const idOptions = computed<LISASelectOption[]>(() => {
  const options =
    props.mode === FilterEntity.EVENT
      ? (boardFilterOptions?.options.value.eventIds ?? [])
      : (boardFilterOptions?.options.value.actionIds ?? []);
  return (
    selectedType.value == null
      ? options
      : options.filter((option) => option.typeId === selectedType.value)
  ).map((option) => ({ id: option.value, label: String(option.label) }));
});

function loadOptions() {
  boardFilterOptions?.load();
}

onMounted(loadOptions);
watch(() => boardFilterOptions?.queryKey.value, loadOptions);

function getFilter() {
  return selectedIDs.value.length === 0 && selectedType.value == null
    ? null
    : new IDFilter(selectedIDs.value, selectedType.value);
}
function applyFilter(filter: IDFilter) {
  selectedIDs.value = filter.ids;
  selectedType.value = filter.typeId;
}
defineExpose({
  getFilter,
  applyFilter,
});

const { t } = useTranslation();
</script>

<style lang="scss" scoped></style>
